import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { auth, db } from '@/lib/supabase';
import { useAuthStore } from '@/stores';
import { Button, Input } from '@/components/ui';

const loginSchema = z.object({
  email: z.string().email('Email non valida'),
  password: z.string().min(6, 'La password deve avere almeno 6 caratteri'),
});

type LoginFormData = z.infer<typeof loginSchema>;

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const { setUser } = useAuthStore();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setError(null);

    try {
      const { data: authData, error: authError } = await auth.signIn(data.email, data.password);

      if (authError) {
        setError(authError.code === 'email_not_confirmed'
          ? 'Email non confermata. Per le prove usa l’account di test già attivato.'
          : authError.code === 'invalid_credentials'
            ? 'Email o password non corrette.'
            : 'Accesso non disponibile. Riprova tra qualche minuto.');
        return;
      }

      if (authData?.user) {
        const { data: profile, error: profileError } = await db.profiles.get(authData.user.id);
        if (profile) {
          setUser(profile);
          navigate('/app/dashboard');
        } else {
          await auth.signOut();
          setError(profileError ? 'Impossibile caricare il profilo. Riprova.' : 'Account valido, ma il profilo professionale non è disponibile. Contatta l’assistenza per completare l’attivazione.');
        }
      }
    } catch {
      setError('Errore durante il login');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f6f7f3] p-4 sm:p-8">
      <div className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-6xl items-center justify-center">
        <div className="grid w-full overflow-hidden rounded-[1.75rem] border border-[#e2e8e2] bg-[#fffefa] shadow-[0_24px_80px_-36px_rgba(20,61,51,.28)] lg:grid-cols-[1fr_.82fr]">
          <aside className="relative hidden overflow-hidden bg-[#0f5b53] p-10 text-white lg:flex lg:flex-col lg:justify-between">
            <div className="pointer-events-none absolute -right-24 top-20 h-80 w-80 rounded-full border border-white/10" />
            <div className="pointer-events-none absolute -right-12 top-32 h-56 w-56 rounded-full border border-white/10" />
            <Link to="/" className="relative flex items-center gap-3 text-sm font-semibold tracking-[.18em]"><span className="grid h-10 w-10 place-items-center rounded-xl bg-white/15 text-lg">N.</span>NUTRY <span className="font-normal tracking-normal text-white/65">Studio</span></Link>
            <div className="relative max-w-md py-12">
              <p className="text-xs font-semibold uppercase tracking-[.16em] text-[#b9ddd0]">Il lavoro clinico, con più ordine</p>
              <h1 className="mt-5 text-4xl font-semibold leading-tight tracking-[-.035em]">Riparti dal punto in cui sei arrivato.</h1>
              <p className="mt-5 leading-7 text-white/75">Pazienti, misurazioni, piani alimentari e appuntamenti raccolti nel tuo spazio di lavoro.</p>
            </div>
            <p className="relative text-xs text-white/55">Uno spazio di lavoro per il tuo studio</p>
          </aside>
          <div className="flex items-center justify-center p-5 sm:p-10 lg:p-14">
            <div className="w-full max-w-md">
              <Link to="/" className="mb-9 inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 lg:hidden"><span className="grid h-8 w-8 place-items-center rounded-lg bg-teal-600 font-bold text-white">N.</span>NUTRY Studio</Link>
              <p className="text-xs font-semibold uppercase tracking-[.14em] text-teal-700">Bentornato</p>
              <h2 className="mt-2 text-3xl font-semibold tracking-tight text-gray-900">Accedi al tuo spazio</h2>
              <p className="mt-2 text-sm text-gray-500">Inserisci le credenziali del tuo account professionale.</p>

              <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-5">
                {error && <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
                <Input label="Email" type="email" placeholder="nome@esempio.it" autoComplete="email" error={errors.email?.message} {...register('email')} />
                <div>
                  <Input label="Password" type="password" placeholder="••••••••" autoComplete="current-password" error={errors.password?.message} {...register('password')} />
                  <div className="mt-2 text-right text-sm"><Link to="/forgot-password" className="font-medium text-teal-700 hover:text-teal-900">Password dimenticata?</Link></div>
                </div>
                <Button type="submit" className="w-full py-3" isLoading={isLoading}>Accedi</Button>
              </form>
              <div className="mt-7 border-t border-gray-100 pt-5 text-center text-sm"><span className="text-gray-600">Non hai un account?</span>{' '}<Link to="/register" className="font-semibold text-teal-700 hover:text-teal-900">Registrati</Link></div>
              <p className="mt-8 text-center text-xs text-gray-500">© {new Date().getFullYear()} NUTRY · by 3asy.app</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
